import { useMemo, useState, type FormEvent } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  getBackfillStatus,
  listBackfillDays,
  retryBackfillErrors,
  runBackfill,
  seedBackfill,
  type BackfillDay,
  type BackfillDayStatus,
  type BackfillDaysFilter,
  type BackfillDaysSortKey,
  type BackfillSummary,
} from "@/api/backfill";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { Pagination } from "@/components/Pagination";
import { Spinner } from "@/components/Spinner";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDateTime, fmtNumber } from "@/lib/format";

const STATUS_QUERY_KEY = ["backfill", "status"] as const;

// Sort options — values must match
// `backend/app/schemas/backfill.py::BackfillDaysSortKey`.
const SORT_OPTIONS: { value: BackfillDaysSortKey; label: string }[] = [
  { value: "date_desc", label: "Date (newest first)" },
  { value: "date_asc", label: "Date (oldest first)" },
  { value: "attempts_desc", label: "Attempts (most first)" },
  { value: "completed_desc", label: "Completed (newest first)" },
  { value: "completed_asc", label: "Completed (oldest first)" },
];

const STATUS_OPTIONS: { value: "" | BackfillDayStatus; label: string }[] = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "running", label: "Running" },
  { value: "done", label: "Done" },
  { value: "error", label: "Error" },
];

const PER_PAGE = 25;
const DEFAULT_SORT: BackfillDaysSortKey = "date_desc";

export function Backfill() {
  const queryClient = useQueryClient();

  // --- Summary / live status (fast poll) ----------------------------
  const { data: status, error: statusError, isLoading: statusLoading } =
    useQuery({
      queryKey: STATUS_QUERY_KEY,
      queryFn: getBackfillStatus,
      // Poll fast while a run is active so the UI feels live; slow
      // it down otherwise to save round-trips.
      refetchInterval: (q) => {
        const v = q.state.data;
        return v?.active || (v?.summary.running ?? 0) > 0 ? 1500 : 5_000;
      },
    });

  // --- Paginated / searchable day list ------------------------------
  const [daysFilter, setDaysFilter] = useState<BackfillDaysFilter>({
    sort: DEFAULT_SORT,
    page: 1,
    per_page: PER_PAGE,
  });
  // Local mirror of the search input so we don't refetch on every
  // keystroke — committed on submit.
  const [qInput, setQInput] = useState("");
  const [statusInput, setStatusInput] = useState<"" | BackfillDayStatus>("");

  // If the running section moves, we want the table to refresh too
  // — tie it to the backfill active flag so a live run bumps the
  // table every ~2s.
  const daysKey = useMemo(
    () => ["backfill", "days", daysFilter, status?.active ?? false] as const,
    [daysFilter, status?.active],
  );

  const {
    data: daysPage,
    error: daysError,
    isLoading: daysLoading,
    isFetching: daysFetching,
  } = useQuery({
    queryKey: daysKey,
    queryFn: () => listBackfillDays(daysFilter),
    placeholderData: keepPreviousData,
    refetchInterval: status?.active ? 2_000 : false,
  });

  // --- Seed / run / retry mutations ---------------------------------
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [notice, setNotice] = useState<string | null>(null);

  function invalidateAll() {
    queryClient.invalidateQueries({ queryKey: STATUS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ["backfill", "days"] });
  }

  const seedMutation = useMutation({
    mutationFn: () => seedBackfill({ start, end }),
    onSuccess: (resp) => {
      setNotice(`Seeded ${resp.inserted} day(s).`);
      invalidateAll();
    },
  });

  const runMutation = useMutation({
    mutationFn: runBackfill,
    onSuccess: (resp) => {
      setNotice(resp.message);
      invalidateAll();
    },
  });

  const retryMutation = useMutation({
    mutationFn: retryBackfillErrors,
    onSuccess: (resp) => {
      setNotice(`Re-queued ${resp.requeued} day(s) for retry.`);
      invalidateAll();
    },
  });

  function onSeedSubmit(e: FormEvent) {
    e.preventDefault();
    setNotice(null);
    seedMutation.mutate();
  }

  function onFilterSubmit(e: FormEvent) {
    e.preventDefault();
    setDaysFilter((f) => ({
      ...f,
      q: qInput.trim() || undefined,
      status: statusInput || undefined,
      page: 1,
    }));
  }

  function resetFilter() {
    setQInput("");
    setStatusInput("");
    setDaysFilter({ sort: DEFAULT_SORT, page: 1, per_page: PER_PAGE });
  }

  const anyError =
    statusError ||
    daysError ||
    seedMutation.error ||
    runMutation.error ||
    retryMutation.error;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Backfill"
        subtitle="Seed historical date ranges, drive a backfill run, and retry failures"
        actions={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => retryMutation.mutate()}
              disabled={retryMutation.isPending}
            >
              {retryMutation.isPending && <Spinner size="sm" />}
              Retry errors
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => runMutation.mutate()}
              disabled={runMutation.isPending || status?.active}
              title={
                status?.active
                  ? "A backfill run is already in progress"
                  : "Start a background backfill run"
              }
            >
              {(runMutation.isPending || status?.active) && (
                <Spinner size="sm" />
              )}
              {status?.active ? "Running…" : "Run backfill"}
            </button>
          </>
        }
      />

      {anyError && (
        <ErrorBanner
          error={anyError}
          onDismiss={() => {
            seedMutation.reset();
            runMutation.reset();
            retryMutation.reset();
          }}
        />
      )}
      {notice && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      {/* Summary cards */}
      {statusLoading && !status && (
        <div className="flex items-center gap-2 text-slate-500">
          <Spinner /> Loading status…
        </div>
      )}
      {status && (
        <SummaryGrid summary={status.summary} active={status.active} />
      )}

      {/* Currently running — only when there IS something running */}
      {status && (status.active || status.running_days.length > 0) && (
        <RunningSection
          days={status.running_days}
          active={status.active}
        />
      )}

      {/* Seed form */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Seed a date range</div>
          <span className="text-xs text-slate-500">
            Inclusive. End must be strictly before today (UTC).
          </span>
        </div>
        <div className="card-body">
          <form
            onSubmit={onSeedSubmit}
            className="grid grid-cols-1 gap-3 md:grid-cols-3"
          >
            <div>
              <label className="label">Start (YYYY-MM-DD)</label>
              <input
                type="date"
                required
                className="input"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div>
              <label className="label">End (YYYY-MM-DD)</label>
              <input
                type="date"
                required
                className="input"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                className="btn-primary w-full"
                disabled={seedMutation.isPending || !start || !end}
              >
                {seedMutation.isPending && <Spinner size="sm" />}
                Seed range
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Paginated, searchable history */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Backfill history</div>
          <span className="text-xs text-slate-500">
            {daysPage
              ? `${daysPage.total.toLocaleString("en-US")} day(s) total`
              : "—"}
          </span>
        </div>

        {/* Filter bar */}
        <form
          onSubmit={onFilterSubmit}
          className="grid grid-cols-1 gap-3 border-b border-slate-200 p-4 md:grid-cols-4"
        >
          <div>
            <label className="label">Search date</label>
            <input
              className="input"
              type="search"
              placeholder="2025, 2025-10, 2025-10-15…"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Status</label>
            <select
              className="input"
              value={statusInput}
              onChange={(e) =>
                setStatusInput(e.target.value as "" | BackfillDayStatus)
              }
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Sort by</label>
            <select
              className="input"
              value={daysFilter.sort ?? DEFAULT_SORT}
              onChange={(e) =>
                setDaysFilter((f) => ({
                  ...f,
                  sort: e.target.value as BackfillDaysSortKey,
                  page: 1,
                }))
              }
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={resetFilter}
              className="btn-secondary flex-1"
            >
              Reset
            </button>
            <button type="submit" className="btn-primary flex-1">
              Apply
            </button>
          </div>
        </form>

        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th className="text-right">Leads</th>
                  <th className="text-right">Attempts</th>
                  <th>Completed</th>
                  <th>Last error</th>
                </tr>
              </thead>
              <tbody>
                {daysLoading && (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-slate-500">
                      <Spinner /> Loading…
                    </td>
                  </tr>
                )}
                {!daysLoading && daysPage?.items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-slate-500">
                      No days match your filters.
                    </td>
                  </tr>
                )}
                {!daysLoading &&
                  daysPage?.items.map((d) => (
                    <tr key={d.date}>
                      <td className="font-mono text-slate-800">{d.date}</td>
                      <td>
                        <StatusBadge status={d.status} />
                      </td>
                      <td className="text-right tabular-nums">
                        {fmtNumber(d.leads_count)}
                      </td>
                      <td className="text-right tabular-nums">
                        {d.attempts}
                      </td>
                      <td className="text-slate-600">
                        {fmtDateTime(d.completed_at)}
                      </td>
                      <td
                        className="max-w-xs truncate text-rose-700"
                        title={d.last_error ?? undefined}
                      >
                        {d.last_error ?? "—"}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {daysPage && (
          <div className="border-t border-slate-200 px-4 py-3">
            <Pagination
              page={daysPage.page}
              pages={daysPage.pages}
              total={daysPage.total}
              onChange={(p) =>
                setDaysFilter((f) => ({ ...f, page: p }))
              }
            />
          </div>
        )}

        {daysFetching && !daysLoading && (
          <div className="px-4 pb-3 text-xs text-slate-400">Refreshing…</div>
        )}
      </div>
    </div>
  );
}

function SummaryGrid({
  summary,
  active,
}: {
  summary: BackfillSummary;
  active: boolean;
}) {
  const cards: { label: string; value: number; tone: string }[] = [
    { label: "Total seeded", value: summary.total, tone: "text-slate-900" },
    { label: "Pending", value: summary.pending, tone: "text-amber-700" },
    { label: "Running", value: summary.running, tone: "text-sky-700" },
    { label: "Done", value: summary.done, tone: "text-emerald-700" },
    { label: "Errors", value: summary.error, tone: "text-rose-700" },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
      {cards.map((c) => (
        <div key={c.label} className="card">
          <div className="card-body">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
              {c.label}
            </div>
            <div
              className={`mt-1 text-2xl font-semibold tabular-nums ${c.tone}`}
            >
              {fmtNumber(c.value)}
            </div>
            {c.label === "Running" && active && (
              <div className="mt-1 text-[11px] text-sky-700">
                Worker active in background
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Live "currently running" section — highlighted so operators can
 * see at a glance what the worker is chewing on. Updates with the
 * same fast-poll as the summary grid.
 */
function RunningSection({
  days,
  active,
}: {
  days: BackfillDay[];
  active: boolean;
}) {
  return (
    <div className="card border-sky-200 ring-1 ring-sky-100">
      <div className="card-header bg-sky-50">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-sky-500" />
          </span>
          <div className="card-title text-sky-900">
            {days.length === 0
              ? "Worker active — between days"
              : days.length === 1
                ? "Currently running"
                : `Currently running (${days.length})`}
          </div>
        </div>
        {active && (
          <span className="text-xs text-sky-700">Auto-refresh 1.5s</span>
        )}
      </div>
      {days.length > 0 && (
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="text-right">Attempt</th>
                  <th className="text-right">Leads so far</th>
                  <th>Last error (previous attempt)</th>
                </tr>
              </thead>
              <tbody>
                {days.map((d) => (
                  <tr key={d.date}>
                    <td className="font-mono font-semibold text-sky-900">
                      {d.date}
                    </td>
                    <td className="text-right tabular-nums text-slate-700">
                      #{d.attempts}
                    </td>
                    <td className="text-right tabular-nums text-slate-700">
                      {fmtNumber(d.leads_count)}
                    </td>
                    <td
                      className="max-w-md truncate text-xs text-slate-500"
                      title={d.last_error ?? undefined}
                    >
                      {d.last_error ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
